/**
 * Shared CLI formatting utilities for status display, durations, and timestamps.
 *
 * @module CLIFormat
 */

export const STATUS_ICONS: Record<string, string> = {
  completed: '\u2705',
  completed_with_errors: '\u26A0\uFE0F',
  success: '\u2705',
  passed: '\u2705',
  failed: '\u274C',
  running: '\uD83D\uDD35',
  queued: '\u23F3',
  canceled: '\uD83D\uDEAB',
  timeout: '\u23F0',
  skipped: '\u23ED\uFE0F',
  flaky_success: '\uD83D\uDFE1',
  pending: '\u23F3',
};

export function statusIcon(status: string): string {
  return STATUS_ICONS[status] ?? '\u2753';
}

export function formatRelative(iso: string | null): string {
  if (!iso) return '\u2014';
  const diffMs = Date.now() - new Date(iso).getTime();
  if (diffMs < 0) return 'just now';
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function computeDuration(startedAt: string | null, finishedAt: string | null): string {
  if (!startedAt || !finishedAt) return '\u2014';
  const ms = new Date(finishedAt).getTime() - new Date(startedAt).getTime();
  if (ms < 0) return '\u2014';
  const totalSeconds = Math.floor(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes < 60) return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainMins = minutes % 60;
  return remainMins > 0 ? `${hours}h ${remainMins}m` : `${hours}h`;
}

export function pad(str: string, len: number): string {
  return str.padEnd(len);
}

export function readFromStdin(): Promise<string> {
  const stdin = process.stdin;

  if (stdin.isTTY) {
    return Promise.reject(
      new Error('No input on stdin. Pipe JSON or use --from-file.')
    );
  }

  const chunks: Buffer[] = [];
  return new Promise((resolve, reject) => {
    stdin.on('data', (chunk) => {
      chunks.push(typeof chunk === 'string' ? Buffer.from(chunk) : chunk);
    });
    stdin.on('end', () => resolve(Buffer.concat(chunks).toString('utf-8')));
    stdin.on('error', reject);
  });
}
